import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, projectScope, BlastResponse, ImpactedSymbol, Repo, SymbolHit } from "../api/client";
import { ImpactedTable } from "../components/ImpactedTable";
import { pagesApi } from "../api/pagesApi";
import type { DeadCodeResult, CommunityData } from "../types/pages";
import CustomButton from "../components/CustomButton";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

type Tab = "blast" | "diff" | "dead-code" | "communities";

const TABS: { id: Tab; label: string }[] = [
  { id: "blast", label: "Blast Radius" },
  { id: "diff", label: "Diff Impact" },
  { id: "dead-code", label: "Dead Code" },
  { id: "communities", label: "Communities" },
];

function isValidTab(s: string | null): s is Tab {
  return TABS.some((t) => t.id === s);
}

export function AnalysisPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const [tab, setTab] = useState<Tab>(isValidTab(urlTab) ? urlTab : "blast");

  // Sync tab when navigated to with ?tab= from another page
  useEffect(() => {
    const t = searchParams.get("tab");
    if (isValidTab(t)) setTab(t);
  }, [searchParams]);

  function switchTab(t: Tab) {
    setTab(t);
    setSearchParams({ tab: t }, { replace: true });
  }

  return (
    <div className="content">
      <div className="tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            className={`tab-btn${tab === t.id ? " active" : ""}`}
            onClick={() => switchTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "blast" && <BlastTab />}
      {tab === "diff" && <DiffTab />}
      {tab === "dead-code" && <DeadCodeTab />}
      {tab === "communities" && <CommunitiesTab />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Blast Radius tab
// ---------------------------------------------------------------------------

function BlastTab() {
  const { projectId = "", repoId = "" } = useParams();
  const [q, setQ] = useState("");
  const [maxDepth, setMaxDepth] = useState(3);
  const [result, setResult] = useState<BlastResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function findAndCompute(e: FormEvent) {
    e.preventDefault();
    if (!q.trim()) return;
    setBusy(true); setErr(null); setResult(null);
    try {
      const list = await api.get<SymbolHit[]>(projectScope(`/repos/${repoId}/symbols?q=${encodeURIComponent(q)}&limit=10`, projectId));
      if (!list[0]) { setErr("No match found."); return; }
      const seed = list[0];
      const r = await api.post<BlastResponse>(projectScope(`/repos/${repoId}/blast-radius`, projectId), {
        target: { kind: seed.kind, id: seed.id, file_path: seed.file_path },
        max_depth: maxDepth,
        include_tests: true,
        include_amplifiers: true,
      });
      setResult(r);
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <div>
      <form className="row card" onSubmit={findAndCompute} style={{ marginBottom: 16 }}>
        <input style={{ flex: 1 }} placeholder="Seed: function or class name" value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="row">
          depth
          <input type="number" min={1} max={6} value={maxDepth} onChange={(e) => setMaxDepth(Number(e.target.value))} style={{ width: 60 }} />
        </label>
        <CustomButton loading={busy} disabled={!q.trim()} buttonText="Compute" buttonTextLoading="Computing" handleSubmitClick={() => {}} />
      </form>
      {err && <div style={{ marginTop: 8 }}><span className="pill danger">{err}</span></div>}

      {result && (
        <div className="card">
          <div className="row" style={{ marginBottom: 16 }}>
            <span className={`pill ${result.risk.band === "HIGH" ? "danger" : result.risk.band === "MED" ? "warn" : "ok"}`}>
              risk {result.risk.score} · {result.risk.band}
            </span>
            <span className="muted">
              {result.impacted_symbols.length} impacted symbols · {result.impacted_tests.length} tests · {result.amplifiers.length} amplifiers
            </span>
          </div>
          <ImpactedTable title="Symbols" rows={result.impacted_symbols} />
          <ImpactedTable title="Tests" rows={result.impacted_tests} />
          {result.amplifiers.length > 0 && (
            <>
              <h4>Amplifiers</h4>
              <ul>
                {result.amplifiers.map((a) => (
                  <li key={a.id}>
                    <span className="pill warn">fan-in {a.fan_in}</span> {a.id} <span className="muted">— {a.reason}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Diff Impact tab
// ---------------------------------------------------------------------------

type RiskLevel = "LOW" | "MEDIUM" | "HIGH";

function classifySymbolRisk(symbol: ImpactedSymbol, amplifierIds: Set<string>): RiskLevel {
  if (amplifierIds.has(symbol.id) || symbol.depth <= 1) return "HIGH";
  if (symbol.depth === 2) return "MEDIUM";
  return "LOW";
}

function getRiskPillClass(level: RiskLevel): string {
  if (level === "HIGH") return "danger";
  if (level === "MEDIUM") return "warn";
  return "ok";
}

function groupByCommunity(
  symbols: ImpactedSymbol[],
  communityMap: Map<string, number | null>
): { communityId: number | null; count: number }[] {
  const groups = new Map<number | null, number>();
  for (const sym of symbols) {
    const cid = communityMap.get(sym.id) ?? null;
    groups.set(cid, (groups.get(cid) ?? 0) + 1);
  }
  return Array.from(groups.entries())
    .map(([communityId, count]) => ({ communityId, count }))
    .sort((a, b) => b.count - a.count);
}

interface ExtendedBlast extends BlastResponse {
  community_map?: Record<string, number | null>;
  cross_service_symbol_ids?: string[];
}

function extractRepoSlug(url: string): string | null {
  const m = url.match(/(?:https?:\/\/[^/]+\/|git@[^:]+:)([^/]+\/[^/]+?)(?:\.git)?\s*$/i);
  return m ? m[1].toLowerCase() : null;
}

function DiffTab() {
  const { projectId = "", repoId = "" } = useParams();
  const navigate = useNavigate();
  const [base, setBase] = useState("");
  const [head, setHead] = useState("");
  const [prUrl, setPrUrl] = useState("");
  const [result, setResult] = useState<ExtendedBlast | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [repoOriginUrl, setRepoOriginUrl] = useState<string | null>(null);

  // Fetch the repo's origin URL on mount so we can validate PR URLs against it
  useEffect(() => {
    if (!repoId) return;
    api.get<Repo[]>(projectScope("/repos", projectId))
      .then((repos) => {
        const repo = repos.find((r) => r.id === repoId);
        if (repo) setRepoOriginUrl(repo.repo_url);
      })
      .catch(() => { /* non-critical — will fall back to blocking in compute */ });
  }, [repoId, projectId]);

  async function compute() {
    setBusy(true); setErr(null);
    try {
      let target: Record<string, unknown>;
      if (prUrl.trim()) {
        const m = prUrl.match(/^(https?:\/\/[^/]+\/[^/]+\/[^/]+)\/pull\/(\d+)/);
        if (!m) throw new Error("Could not parse PR URL");

        // Validate that the PR URL matches this repo's origin
        const prSlug = extractRepoSlug(m[1]);
        if (!repoOriginUrl) {
          throw new Error("Unable to verify repository origin. Please try again or use base/head SHAs instead.");
        }
        const repoSlug = extractRepoSlug(repoOriginUrl);
        if (prSlug && repoSlug && prSlug !== repoSlug) {
          throw new Error(
            `PR URL does not match this repository. Expected "${repoSlug}" but got "${prSlug}".`
          );
        }

        target = { kind: "pr", repo_url: m[1], pr_number: Number(m[2]) };
      } else if (base && head) {
        target = { kind: "diff", base_sha: base, head_sha: head };
      } else {
        throw new Error("Provide a PR URL or base+head SHAs");
      }
      const r = await api.post<ExtendedBlast>(projectScope(`/repos/${repoId}/blast-radius`, projectId), {
        target, max_depth: 3, include_tests: true, include_amplifiers: true,
      });
      setResult(r);
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  const amplifierIds = new Set(result?.amplifiers?.map((a) => a.id) ?? []);
  const communityMap = new Map<string, number | null>(
    Object.entries(result?.community_map ?? {}).map(([k, v]) => [k, v])
  );
  const crossServiceIds = new Set(result?.cross_service_symbol_ids ?? []);
  const communities = result ? groupByCommunity(result.impacted_symbols, communityMap) : [];

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Diff impact</h3>
        <p className="muted">Paste a GitHub PR URL, or supply explicit base/head SHAs.</p>
        <div className="row">
          <input style={{ flex: 2 }} placeholder={repoOriginUrl ? `https://github.com/${extractRepoSlug(repoOriginUrl) ?? "org/repo"}/pull/123` : "https://github.com/org/repo/pull/123"} value={prUrl} onChange={(e) => setPrUrl(e.target.value)} />
          <span className="muted">or</span>
          <input style={{ flex: 1 }} placeholder="base sha" value={base} onChange={(e) => setBase(e.target.value)} />
          <input style={{ flex: 1 }} placeholder="head sha" value={head} onChange={(e) => setHead(e.target.value)} />
          <CustomButton loading={busy} buttonText="Compute" buttonTextLoading="Computing" handleSubmitClick={compute} />
        </div>
        {err && <div className="pill danger" style={{ marginTop: 8 }}>{err}</div>}
      </div>

      {result && (
        <div className="card">
          <div className="row" style={{ marginBottom: 16, alignItems: "center", gap: 16 }}>
            <RiskGauge score={result.risk.score} band={result.risk.band} />
            {result?.impacted_symbols?.length > 0 && <CustomButton loading={false} buttonText="View in 3D" buttonTextLoading="" handleSubmitClick={() => {
              const ids = result.impacted_symbols.map((s) => s.id).join(",");
              navigate(`/projects/${projectId}/repos/${repoId}/explore?highlight=${encodeURIComponent(ids)}`);
            }} />}
          </div>

          {communities.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <h4>Affected communities</h4>
              <div className="row" style={{ flexWrap: "wrap" }}>
                {communities.map(({ communityId, count }) => (
                  <span key={communityId ?? "none"} className="pill">
                    {communityId !== null ? `Community ${communityId}` : "Unassigned"}: {count}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <h4>Impacted symbols <span className="muted">({result.impacted_symbols.length})</span></h4>
            <Card className="orgs-create-card">
              <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell className="projects-th">Symbol</TableCell>
                        <TableCell className="projects-th">Kind</TableCell>
                        <TableCell className="projects-th">Risk</TableCell>
                        <TableCell className="projects-th">Depth</TableCell>
                        <TableCell className="projects-th">File</TableCell>
                        <TableCell className="projects-th">Flags</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.impacted_symbols.map((r) => {
                        const risk = classifySymbolRisk(r, amplifierIds);
                        return (
                          <TableRow key={r.id} className="projects-row"
                            onClick={() => navigate(`/projects/${projectId}/repos/${repoId}/explore?symbol=${encodeURIComponent(r.fq_name)}`)}
                            sx={{ cursor: "pointer" }} role="button" tabIndex={0}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/projects/${projectId}/repos/${repoId}/explore?symbol=${encodeURIComponent(r.fq_name)}`); } }}>
                            <TableCell className="projects-td" sx={{ fontFamily: "'SF Mono', 'Fira Code', monospace !important", color: "var(--brand-pink) !important" }}>{r.fq_name}</TableCell>
                            <TableCell className="projects-td"><span className="pill">{r.kind}</span></TableCell>
                            <TableCell className="projects-td"><span className={`pill ${getRiskPillClass(risk)}`}>{risk}</span></TableCell>
                            <TableCell className="projects-td">{r.depth}</TableCell>
                            <TableCell className="projects-td" sx={{ fontFamily: "'SF Mono', 'Fira Code', monospace !important", color: "var(--brand-pink) !important" }}>{r.file_path}</TableCell>
                            <TableCell className="projects-td">
                              {crossServiceIds.has(r.id) && (
                                <span className="pill danger">Cross-service</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {result.impacted_symbols.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} className="projects-td" sx={{ textAlign: "center", color: "rgba(120,120,125,1) !important" }}>None.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </div>

          <div style={{ marginTop: 16 }}>
            <h4>Impacted tests <span className="muted">({result.impacted_tests.length})</span></h4>
            <Card className="orgs-create-card">
              <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell className="projects-th">Test</TableCell>
                        <TableCell className="projects-th">Kind</TableCell>
                        <TableCell className="projects-th">Depth</TableCell>
                        <TableCell className="projects-th">File</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.impacted_tests.map((t) => (
                        <TableRow key={t.id} className="projects-row">
                          <TableCell className="projects-td" sx={{ fontFamily: "'SF Mono', 'Fira Code', monospace !important", color: "var(--brand-pink) !important" }}>{t.fq_name}</TableCell>
                          <TableCell className="projects-td"><span className="pill">{t.kind}</span></TableCell>
                          <TableCell className="projects-td">{t.depth}</TableCell>
                          <TableCell className="projects-td" sx={{ fontFamily: "'SF Mono', 'Fira Code', monospace !important", color: "var(--brand-pink) !important" }}>{t.file_path}</TableCell>
                        </TableRow>
                      ))}
                      {result.impacted_tests.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} className="projects-td" sx={{ textAlign: "center", color: "rgba(120,120,125,1) !important" }}>None.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </div>

          <div style={{ marginTop: 16 }}>
            <h4>Files</h4>
            <Card className="orgs-create-card">
              <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell className="projects-th">File</TableCell>
                        <TableCell className="projects-th"># Symbols</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.impacted_files.map((f) => (
                        <TableRow key={f.path} className="projects-row">
                          <TableCell className="projects-td" sx={{ fontFamily: "monospace" }}>{f.path}</TableCell>
                          <TableCell className="projects-td">{f.symbol_count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

function RiskGauge({ score, band }: { score: number; band: "LOW" | "MED" | "HIGH" }) {
  const bandLabel = band === "MED" ? "MEDIUM" : band;
  const color = band === "HIGH" ? "var(--danger)" : band === "MED" ? "var(--warn)" : "var(--ok)";
  const pillClass = band === "HIGH" ? "danger" : band === "MED" ? "warn" : "ok";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      {score > 0 && <div style={{ position: "relative", width: 160, height: 12, background: "var(--border)", borderRadius: 6, overflow: "hidden" }}>
        <div data-testid="risk-gauge-fill" style={{ width: `${Math.min(100, score)}%`, height: "100%", background: color, borderRadius: 6, transition: "width 0.3s ease" }} />
      </div>}
      <span className={`pill ${pillClass}`}>{score} · {bandLabel}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dead Code tab
// ---------------------------------------------------------------------------

type SortField = "name" | "kind" | "file_path" | "language" | "confidence";
type SortDir = "asc" | "desc";

function DeadCodeTab() {
  const { repoId = "", projectId = "" } = useParams();
  const navigate = useNavigate();
  const [results, setResults] = useState<DeadCodeResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [threshold, setThreshold] = useState(0.5);
  const [sortField, setSortField] = useState<SortField>("confidence");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    pagesApi.getDeadCode(repoId, projectId)
      .then((data) => { if (!cancelled) setResults(data); })
      .catch((e) => { if (!cancelled) setError((e as Error).message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [repoId, projectId]);

  const sorted = useMemo(() => {
    const filtered = results.filter((r) => r.confidence >= threshold);
    const copy = [...filtered];
    copy.sort((a, b) => {
      const av = a[sortField], bv = b[sortField];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return sortDir === "asc" ? av - bv : bv - av;
      return sortDir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return copy;
  }, [results, threshold, sortField, sortDir]);

  function handleSort(field: SortField) {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  }

  function sortIndicator(field: SortField) {
    if (sortField !== field) return "";
    return sortDir === "asc" ? " ▲" : " ▼";
  }

  if (loading) return <div className="card"><p className="muted">Loading...</p></div>;
  if (error) return <div className="card"><div className="pill danger">{error}</div></div>;

  return (
    <div className="card">
      <Typography variant="body2" className="muted" sx={{ mb: 1 }}>
        Showing {sorted.length} of {results.length} dead code candidates
      </Typography>
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: "var(--brand-black)", mb: 1 }}>
          Confidence threshold: {(threshold * 100).toFixed(0)}%
        </Typography>
        <Slider
          id="confidence-threshold"
          value={threshold}
          onChange={(_e, val) => setThreshold(val as number)}
          min={0}
          max={1}
          step={0.01}
          aria-label="Confidence threshold"
          sx={{
            color: "var(--brand-pink)",
            "& .MuiSlider-thumb": { width: 18, height: 18, backgroundColor: "#fff", border: "2px solid var(--brand-pink)" },
            "& .MuiSlider-track": { height: 6 },
            "& .MuiSlider-rail": { height: 6, backgroundColor: "var(--border-light)" },
          }}
        />
      </Box>
      {sorted.length === 0 ? (
        <p className="muted">No dead code candidates match the current threshold.</p>
      ) : (
        <Card className="orgs-create-card">
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {(["name", "kind", "file_path", "language", "confidence"] as SortField[]).map((f) => (
                      <TableCell key={f} className="projects-th" onClick={() => handleSort(f)} sx={{ cursor: "pointer" }}>
                        {f.replace("_", " ").replace(/^\w/, (c) => c.toUpperCase())}{sortIndicator(f)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sorted.map((item) => (
                    <TableRow key={item.id} className="projects-row"
                      onClick={() => navigate(`/projects/${projectId}/repos/${repoId}/explore?symbol=${encodeURIComponent(item.name)}`)}
                      sx={{ cursor: "pointer" }} role="button" tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/projects/${projectId}/repos/${repoId}/explore?symbol=${encodeURIComponent(item.name)}`); } }}>
                      <TableCell className="projects-td" sx={{ fontFamily: "'SF Mono', 'Fira Code', monospace !important", color: "var(--brand-pink) !important" }}>{item.name}</TableCell>
                      <TableCell className="projects-td">{item.kind}</TableCell>
                      <TableCell className="projects-td" sx={{ fontFamily: "'SF Mono', 'Fira Code', monospace !important", color: "var(--brand-pink) !important" }}>{item.file_path}</TableCell>
                      <TableCell className="projects-td">{item.language ?? "—"}</TableCell>
                      <TableCell className="projects-td">{(item.confidence * 100).toFixed(0)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Communities tab
// ---------------------------------------------------------------------------

function CommunitiesTab() {
  const { projectId = "", repoId = "" } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<CommunityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    pagesApi.getCommunities(repoId, projectId)
      .then((result) => { if (!cancelled) setData(result); })
      .catch((e) => { if (!cancelled) setError((e as Error).message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [repoId, projectId]);

  if (loading) return <div className="card"><p className="muted">Loading...</p></div>;
  if (error) return <div className="card"><div className="pill danger">{error}</div></div>;
  if (!data || data.communities.length === 0) {
    return (
      <div className="card" data-testid="empty-state">
        <p className="muted">No community data available. Run community detection on this repository.</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 24 }} data-testid="community-summary">
        <div style={{ background: "rgba(203, 46, 186, 0.04)", border: "1px solid #e3e3e5", borderRadius: 12, padding: "16px 20px" }}>
          <span className="muted" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Total Clusters</span>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "rgba(21, 0, 39, 1)", marginTop: 4 }}>{data.total_clusters}</div>
        </div>
        <div style={{ background: "rgba(203, 46, 186, 0.04)", border: "1px solid #e3e3e5", borderRadius: 12, padding: "16px 20px" }}>
          <span className="muted" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Avg Cluster Size</span>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "rgba(21, 0, 39, 1)", marginTop: 4 }}>{data.avg_cluster_size.toFixed(1)}</div>
        </div>
        <div style={{ background: "rgba(203, 46, 186, 0.04)", border: "1px solid #e3e3e5", borderRadius: 12, padding: "16px 20px" }}>
          <span className="muted" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Inter-Cluster Edge Density</span>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "rgba(21, 0, 39, 1)", marginTop: 4 }}>{(data.inter_cluster_edge_density * 100).toFixed(1)}%</div>
        </div>
      </div>

      <section data-testid="community-chart">
        <h4>Community Size Distribution</h4>
        <MiniBarChart communities={data.communities} />
      </section>

      <section data-testid="community-list">
        <h4>Community Details</h4>
        <Card className="orgs-create-card">
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className="projects-th" sx={{ width: 150 }}>Community</TableCell>
                    <TableCell className="projects-th">Nodes</TableCell>
                    <TableCell className="projects-th">Languages</TableCell>
                    <TableCell className="projects-th">Members (up to 10)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.communities.map((c) => (
                    <TableRow key={c.id} className="projects-row"
                      onClick={() => navigate(`/projects/${projectId}/repos/${repoId}/explore?community=${c.id}`)}
                      sx={{ cursor: "pointer" }} role="button" tabIndex={0}
                      data-testid={`community-row-${c.id}`}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/projects/${projectId}/repos/${repoId}/explore?community=${c.id}`); } }}>
                      <TableCell className="projects-td">Community {c.id}</TableCell>
                      <TableCell className="projects-td">{c.node_count}</TableCell>
                      <TableCell className="projects-td">{c.languages.join(", ") || "—"}</TableCell>
                      <TableCell className="projects-td" sx={{ fontFamily: "monospace", fontSize: "0.85em" }}>
                        {c.members.slice(0, 10).join(", ")}{c.members.length > 10 && " …"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

function MiniBarChart({ communities }: { communities: CommunityData["communities"] }) {
  if (communities.length === 0) return null;
  const sorted = [...communities].sort((a, b) => b.node_count - a.node_count);
  const maxCount = Math.max(...sorted.map((c) => c.node_count));
  const chartHeight = 180;
  const barGap = 2;
  const barWidth = Math.max(8, Math.floor(400 / sorted.length) - barGap);
  const svgWidth = sorted.length * (barWidth + barGap);
  return (
    <div style={{ width: "100%", height: 200, marginBottom: 24, overflowX: "auto" }} role="img" aria-label="Community size distribution bar chart">
      <svg width={svgWidth} height={chartHeight} data-testid="bar-chart-svg">
        {sorted.map((c, i) => {
          const barHeight = maxCount > 0 ? (c.node_count / maxCount) * (chartHeight - 20) : 0;
          return (
            <g key={c.id}>
              <rect x={i * (barWidth + barGap)} y={chartHeight - barHeight - 20} width={barWidth} height={barHeight} fill="#58a6ff" data-testid={`bar-${c.id}`}>
                <title>Community {c.id}: {c.node_count} nodes</title>
              </rect>
              <text x={i * (barWidth + barGap) + barWidth / 2} y={chartHeight - 4} textAnchor="middle" fontSize="10" fill="#7d8590">{c.id}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
